import { isEmpty, map } from 'es-toolkit/compat';

import {
  CellTableArtifactDiscoveryDependencies,
  CellTableArtifactFile,
  CellTableArtifactDiscoveryProgress,
  discoverCellTableArtifactFiles,
} from 'components/StudiesDashboard/StudyExportMenu/cellTableArtifactDiscovery';
import { CasesParams } from 'utils/useCasesParams/types';
import { describeExportFailure } from './exportErrorReport';

export interface CellTableRunScope {
  // Undefined for Published and Approved results, which are not tied to one run.
  runId?: string;
  casesParams: CasesParams;
}

export interface CellTableRunDiscoveryFailure {
  runId?: string;
  message: string;
}

export interface CellTableRunsDiscovery {
  files: CellTableArtifactFile[];
  failures: CellTableRunDiscoveryFailure[];
  // Runs that were read successfully and have no final cell tables.
  emptyRunIds: string[];
  // Files that shared a URL with another listed file and were listed only once.
  collapsedDuplicates: number;
}

export interface CellTableRunsDiscoveryProgress extends CellTableArtifactDiscoveryProgress {
  runIndex: number;
  runCount: number;
}

type DiscoverFiles = typeof discoverCellTableArtifactFiles;

// Discovers the final cell tables of each scope on its own and lists them together. A scope that fails is
// reported and does not stop the others; cancellation is never reported as a failure.
export const discoverCellTableArtifactFilesForRuns = async ({
  scopes,
  signal,
  discover = discoverCellTableArtifactFiles,
  dependencies,
  onProgress,
}: {
  scopes: CellTableRunScope[];
  signal: AbortSignal;
  discover?: DiscoverFiles;
  dependencies?: CellTableArtifactDiscoveryDependencies;
  onProgress?: (progress: CellTableRunsDiscoveryProgress) => void;
}): Promise<CellTableRunsDiscovery> => {
  const result: CellTableRunsDiscovery = { files: [], failures: [], emptyRunIds: [], collapsedDuplicates: 0 };
  for (const [runIndex, { runId, casesParams }] of scopes.entries()) {
    try {
      const files = await discover(
        casesParams,
        signal,
        dependencies,
        (progress) =>
          onProgress?.({
            ...progress,
            discoveredFiles: result.files.length + progress.discoveredFiles,
            runIndex,
            runCount: scopes.length,
          }),
        {
          onDuplicatesCollapsed: (count) => {
            result.collapsedDuplicates += count;
          },
        }
      );
      // The same artifact id may be found under two runs; the run keeps each entry distinct and visible.
      result.files.push(...(runId ? map(files, (file) => ({ ...file, id: `${runId}:${file.id}` })) : files));
      if (runId && isEmpty(files)) result.emptyRunIds.push(runId);
    } catch (error) {
      if (signal.aborted) throw error;
      result.failures.push({
        runId,
        message: describeExportFailure(error, 'The cell tables could not be loaded.'),
      });
    }
  }
  return result;
};
